import { useCallback, useEffect, useRef, useState } from "react";
import { logoutUser, renewSession } from "../services/authService";
import { clearAuthCookies } from "../utils/authCookies";
import { rememberReturnTo } from "../utils/returnTo";
import {
  IDLE_LIMIT_MS,
  RENEW_EVERY_MS,
  SESSION_ENDED_KEY,
  WARNING_AT_MS,
  hasSignIn,
  idleFor,
  lastActivityAt,
  lastRenewalAt,
  markSessionEnded,
  recordActivity,
  sessionEndedReason,
  setKeeperRunning,
  setLastRenewalAt,
  syncSignIn,
} from "../utils/idleSession";

const TICK_MS = 5 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "wheel", "touchstart", "scroll"];

function currentPath() {
  return `${globalThis.location.pathname}${globalThis.location.search}`;
}

function goToSignIn(reason) {
  if (reason !== "signed_out") rememberReturnTo(currentPath());
  globalThis.location.replace("/");
}

/**
 * Keeps the sign-in alive while the person works and signs them out after
 * an hour without activity. Returns the seconds left once the warning is
 * due (null before then) and a handler for "Stay signed in".
 */
export default function useIdleSignOut(enabled = true) {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const renewing = useRef(false);
  const signingOut = useRef(false);

  const signOut = useCallback(async (reason) => {
    if (signingOut.current) return;
    signingOut.current = true;
    markSessionEnded(reason);
    rememberReturnTo(currentPath());
    try {
      await logoutUser();
    } catch (error) {
      // The server sign-in also lapses on its own after the idle hour.
    }
    clearAuthCookies();
    globalThis.location.replace("/");
  }, []);

  const renew = useCallback(async () => {
    if (renewing.current || signingOut.current) return;
    renewing.current = true;
    const previous = lastRenewalAt();
    setLastRenewalAt(Date.now());
    try {
      await renewSession();
    } catch (error) {
      if (error?.response?.status === 401) {
        signOut("ended");
      } else {
        setLastRenewalAt(previous);
      }
    } finally {
      renewing.current = false;
    }
  }, [signOut]);

  const tick = useCallback(() => {
    if (signingOut.current) return;
    const now = Date.now();
    syncSignIn(now);
    const endedReason = sessionEndedReason();
    if (endedReason) {
      signingOut.current = true;
      goToSignIn(endedReason);
      return;
    }
    // Nobody signed in on this device: the normal sign-in flow handles the
    // page, and a renewal attempt would only fail and look like a lapse.
    if (!hasSignIn()) {
      setSecondsLeft(null);
      return;
    }
    const idle = idleFor(now);
    if (idle >= IDLE_LIMIT_MS) {
      signOut("idle");
      return;
    }
    setSecondsLeft(idle >= WARNING_AT_MS ? Math.ceil((IDLE_LIMIT_MS - idle) / 1000) : null);
    if (lastActivityAt() > lastRenewalAt() && now - lastRenewalAt() >= RENEW_EVERY_MS) {
      renew();
    }
  }, [renew, signOut]);

  const staySignedIn = useCallback(() => {
    recordActivity(Date.now(), { force: true });
    setSecondsLeft(null);
    renew();
  }, [renew]);

  useEffect(() => {
    if (!enabled) {
      setSecondsLeft(null);
      return undefined;
    }
    setKeeperRunning(true);
    const watchedDocuments = [];

    const onActivity = () => {
      if (!recordActivity()) tick();
    };
    const watch = (doc) => {
      if (!doc || watchedDocuments.includes(doc)) return;
      watchedDocuments.push(doc);
      for (const name of ACTIVITY_EVENTS) {
        doc.addEventListener(name, onActivity, { capture: true, passive: true });
      }
    };
    // Documents open in a same-site frame (the PDF viewer); clicks and
    // scrolling there never reach this page, so listen inside the frame.
    const watchFrames = () => {
      for (const frame of document.querySelectorAll("iframe")) {
        try {
          watch(frame.contentDocument);
        } catch (error) {
          // A frame from another site cannot be watched.
        }
      }
    };
    // Focus moving into a frame from another site (an Office editor)
    // is the only sign of work there.
    const onBlur = () => {
      if (document.activeElement?.tagName === "IFRAME") onActivity();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    const onStorage = (event) => {
      if (event.key === SESSION_ENDED_KEY && event.newValue) tick();
    };

    watch(document);
    watchFrames();
    syncSignIn();
    tick();
    const timer = setInterval(() => {
      watchFrames();
      tick();
    }, TICK_MS);
    window.addEventListener("blur", onBlur);
    window.addEventListener("pageshow", tick);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      setKeeperRunning(false);
      clearInterval(timer);
      for (const doc of watchedDocuments) {
        for (const name of ACTIVITY_EVENTS) {
          try {
            doc.removeEventListener(name, onActivity, { capture: true });
          } catch (error) {
            // The frame has gone.
          }
        }
      }
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("pageshow", tick);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, tick]);

  return { secondsLeft, staySignedIn };
}
